import {
  AlertTriangle,
  Banknote,
  Database,
  Globe,
  LayoutDashboard,
  Lightbulb,
  LucideIcon,
  MessageCircle,
  Percent,
  Sunrise,
  TrendingDown
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiAnomalySummary } from '@/components/anomalies/api';
import { fetchDataStatus } from '@/components/data/api';
import {
  CASH_API,
  MARGIN_API,
  SALES_API,
  TODAY_API,
  fetchEngineStatus
} from '@/components/engine/api';
import { fetchProfilePending } from '@/components/know-your-data/api';

import { useFeatures } from '@/hooks/useFeatures';

/**
 * The sections of Signals, shared by the desktop rail (Rail) and the phone
 * tab bar (MobileTabBar).
 *
 * Navigation stays IN THE TAB. The sections used to open a new browser tab
 * each, because leaving the chat was thought to end its sandbox. It does not:
 * the chat socket is opened once by App and is not closed when the route
 * changes, so going to Dashboard and back keeps the same container and the
 * same conversation (an answer still being written keeps arriving). On a
 * phone, a new tab per tap left a pile of tabs behind.
 */

export interface Section {
  key:
    | 'today'
    | 'chat'
    | 'dashboard'
    | 'anomalies'
    | 'market'
    | 'cash'
    | 'sales'
    | 'margin'
    | 'kyd'
    | 'data';
  label: string;
  /** One word for the phone tab bar, where four tabs share ~375px. */
  shortLabel?: string;
  path: string;
  icon: LucideIcon;
  /** Feature flag gating this section; undefined means always on. */
  flag?: string;
  badge?: number | null;
}

const LAST_CHAT_KEY = 'signals.lastChatPath';

export const isChatPath = (pathname: string) =>
  pathname === '/' || pathname.startsWith('/thread');

const readLastChatPath = () => {
  try {
    const p = window.sessionStorage.getItem(LAST_CHAT_KEY);
    return p && isChatPath(p) ? p : '/';
  } catch {
    return '/';
  }
};

export function useSections() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { features, loading } = useFeatures();
  const [openCount, setOpenCount] = useState<number | null>(null);
  // The Cash section is offered by feeds, not by a flag: it shows once the
  // server says the company feeds receivables or cheques (never for admins).
  const [cashOn, setCashOn] = useState(false);
  const [salesOn, setSalesOn] = useState(false);
  const [marginOn, setMarginOn] = useState(false);
  const [todayOn, setTodayOn] = useState(false);
  // Know your data: every company user has it; the server refuses admins, and
  // that refusal is what keeps it out of an admin's navigation.
  const [kydOn, setKydOn] = useState(false);
  // Your data: spreadsheets the company adds. Company users on a tenant
  // that has the page; the server refuses admins and unmigrated tenants.
  const [dataOn, setDataOn] = useState(false);

  const anomaliesOn = !loading && !!features.anomaly_detection;

  // Remember where the chat was, so the Chat section returns to the
  // conversation instead of starting a new one.
  useEffect(() => {
    if (!isChatPath(pathname)) return;
    try {
      window.sessionStorage.setItem(LAST_CHAT_KEY, pathname);
    } catch {
      /* private mode: Chat just opens the home chat */
    }
  }, [pathname]);

  useEffect(() => {
    let cancelled = false;
    fetchEngineStatus(CASH_API)
      .then((s) => {
        if (!cancelled) setCashOn(!!s.available);
      })
      .catch(() => {
        if (!cancelled) setCashOn(false);
      });
    fetchEngineStatus(SALES_API)
      .then((s) => {
        if (!cancelled) setSalesOn(!!s.available);
      })
      .catch(() => {
        if (!cancelled) setSalesOn(false);
      });
    fetchEngineStatus(MARGIN_API)
      .then((s) => {
        if (!cancelled) setMarginOn(!!s.available);
      })
      .catch(() => {
        if (!cancelled) setMarginOn(false);
      });
    fetchEngineStatus(TODAY_API)
      .then((s) => {
        if (!cancelled) setTodayOn(!!s.available);
      })
      .catch(() => {
        if (!cancelled) setTodayOn(false);
      });
    fetchProfilePending()
      .then(() => {
        if (!cancelled) setKydOn(true);
      })
      .catch(() => {
        if (!cancelled) setKydOn(false);
      });
    fetchDataStatus()
      .then((s) => {
        if (!cancelled) setDataOn(!!s.available);
      })
      .catch(() => {
        if (!cancelled) setDataOn(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!anomaliesOn) return;
    let cancelled = false;
    apiAnomalySummary()
      .then((s) => {
        // What the Anomalies page shows, not every open row (see the owner
        // view): a badge of 30 over a page of 9 cannot be reconciled.
        if (!cancelled) setOpenCount(s.owner_open_count ?? s.open_count ?? 0);
      })
      .catch(() => {
        if (!cancelled) setOpenCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [anomaliesOn, pathname]);

  const all: Section[] = [
    // The three engines' findings on one page. Offered when any engine is.
    { key: 'today', label: 'Today', path: '/today', icon: Sunrise },
    { key: 'chat', label: 'Chat', path: '/', icon: MessageCircle },
    {
      key: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      icon: LayoutDashboard,
      flag: 'dashboard'
    },
    {
      key: 'anomalies',
      label: 'Anomalies',
      path: '/anomalies',
      icon: AlertTriangle,
      flag: 'anomaly_detection',
      badge: openCount
    },
    // Who owes the company and the cheques. Offered by feeds (cashOn above).
    {
      key: 'cash',
      label: 'Cash Signals',
      shortLabel: 'Cash',
      path: '/cash',
      icon: Banknote
    },
    // The sales the ERP never records. Offered by feeds (salesOn above).
    {
      key: 'sales',
      label: 'Sales Signals',
      shortLabel: 'Sales',
      path: '/sales',
      icon: TrendingDown
    },
    // Profit leaking between cost and price. Offered by feeds (marginOn above).
    {
      key: 'margin',
      label: 'Margin Signals',
      shortLabel: 'Margin',
      path: '/margin',
      icon: Percent
    },
    // Market and calendar data beside the customer's own figures. No flag,
    // the same as the chat that reads it. Named after the page itself
    // ("External signals"); the phone tab bar uses the one-word label.
    {
      key: 'market',
      label: 'External Signals',
      shortLabel: 'External',
      path: '/external-signals',
      icon: Globe
    },
    // What Signals understands about the company. Company users only (kydOn).
    {
      key: 'kyd',
      label: 'Know your data',
      shortLabel: 'Know',
      path: '/know-your-data',
      icon: Lightbulb
    },
    // The ERP topics and the spreadsheets the company added. Company users
    // on a tenant that has the page (dataOn above).
    {
      key: 'data',
      label: 'Your data',
      shortLabel: 'Data',
      path: '/data',
      icon: Database
    }
  ];

  const sections = loading
    ? []
    : all.filter(
        (s) =>
          (!s.flag || !!(features as any)[s.flag]) &&
          (s.key !== 'cash' || cashOn) &&
          (s.key !== 'sales' || salesOn) &&
          (s.key !== 'margin' || marginOn) &&
          (s.key !== 'today' || todayOn) &&
          (s.key !== 'kyd' || kydOn) &&
          (s.key !== 'data' || dataOn)
      );

  const active: Section['key'] | null = isChatPath(pathname)
    ? 'chat'
    : (sections.find((s) => s.key !== 'chat' && pathname.startsWith(s.path))
        ?.key ?? null);

  const hrefOf = useCallback(
    (s: Section) => (s.key === 'chat' ? readLastChatPath() : s.path),
    []
  );

  const go = useCallback(
    (s: Section) => {
      if (s.key === active) return;
      navigate(hrefOf(s));
    },
    [active, navigate, hrefOf]
  );

  return { sections, active, go, hrefOf };
}

export type SectionsNav = ReturnType<typeof useSections>;

/** The page name for the phone header, where the segmented control is hidden. */
export function pageTitle(pathname: string, nav: SectionsNav): string {
  const s = nav.sections.find((x) => x.key === nav.active);
  if (s) return s.label;
  if (pathname.startsWith('/financial-settings')) return 'Financial settings';
  if (pathname.startsWith('/admin')) return 'Configurations';
  if (pathname.startsWith('/notifications')) return 'Notifications';
  if (pathname.startsWith('/know-your-data')) return 'Know your data';
  if (pathname.startsWith('/business-profile')) return 'Business profile';
  return '';
}

/** Plain left clicks navigate in the tab; Ctrl/Cmd/middle click keep the
 *  browser's own "open in a new tab". */
export const isPlainClick = (e: React.MouseEvent) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
